import { ReactElement, useMemo, useState } from 'react';
import {
  customColorMapping,
  Danger500,
  Neutral500,
  Neutral600,
  Success700A30,
  Success500,
  Warning600
} from '@src/utils/colors';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import {
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  ScatterShapeProps,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { CPX_FORMATTER } from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { ChartContainer } from '@Components/ChartContainer';
import { Design, Size } from '@Components/designConstants';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { TableEmptyState } from '@Components/TableEmptyState';
import {
  ColorMode,
  ColorModeType,
  COLOR_CPX_OPTIONS,
  QuadrantPoint,
  AdSet
} from './meta-fatigue-insights-constants';
import {
  buildQuadrantPoints,
  buildCpxBands,
  CpxBands,
  emptyStateMessage,
  getBubbleColors,
  isFlaggedQuadrantPoint
} from './meta-fatigue-insights-utils';

const TICK_STYLE = { fill: Neutral500, fontSize: 10 };
const GRID_COLOR = Neutral500;
const FLAG_COLOR = Danger500;
const AXIS_TICKS = [0, 0.25, 0.5, 0.75, 1];

const bubbleRadius = (spend: number, maxSpend: number): number =>
  10 + 18 * Math.sqrt(spend / maxSpend);

// Diversity is healthiest in the MIDDLE of the range, not at the top — per Meta's
// creative guidance, so the green zone spans 0.4–0.7 rather than the upper half.
const DIVERSITY_HEALTHY_MIN = 0.4;
const DIVERSITY_HEALTHY_MAX = 0.7;
const FATIGUE_HEALTHY_MAX = 0.5;

// Bands are computed from the data (terciles), so the legend has to be too. Lower CPX is
// better, so the cheapest tercile is green and the priciest is red — inverted from ROAS.
const cpxLegend = (bands: CpxBands): { color: string; label: string }[] => {
  if (!Number.isFinite(bands.watch)) {
    return [{ color: Warning600, label: 'CPX (too few points to rank)' }];
  }
  return [
    { color: Success500, label: `CPX ≤ ${CPX_FORMATTER(bands.healthy)}` },
    {
      color: Warning600,
      label: `CPX ${CPX_FORMATTER(bands.healthy)}–${CPX_FORMATTER(bands.watch)}`
    },
    { color: Danger500, label: `CPX > ${CPX_FORMATTER(bands.watch)}` }
  ];
};

interface QuadrantTooltipProps {
  campaigns: string[];
  active?: boolean;
  payload?: { payload: QuadrantPoint }[];
}

const QuadrantTooltip = ({ campaigns, active, payload }: QuadrantTooltipProps) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className='quadrant-tooltip'>
      <div className='quadrant-tooltip__name'>{point.name}</div>
      <div className='quadrant-tooltip__meta'>{campaigns[point.camp]}</div>
      {/* An imputed axis prints N/A rather than 0.00. Printing 0.00 would make a
          missing reading indistinguishable from a real zero, and a real diversity
          score of 0 is meaningful data. */}
      <div className='quadrant-tooltip__metrics'>
        Fatigue {point.imputedY ? 'N/A' : point.y.toFixed(2)} · Diversity{' '}
        {point.imputedX ? 'N/A' : point.x.toFixed(2)} · CPX {CPX_FORMATTER(point.cpx ?? 0)}
      </div>
    </div>
  );
};

const QuadrantLegend = ({
  campaigns,
  campaignColors,
  colorMode,
  points,
  bands
}: {
  campaigns: string[];
  campaignColors: string[];
  colorMode: ColorModeType;
  points: QuadrantPoint[];
  bands: CpxBands;
}) => {
  const isCampaign = colorMode === ColorMode.CAMPAIGN;
  // Only campaigns actually present on the chart.
  const presentCampaigns = [...new Set(points.map(point => point.camp))];
  const items = isCampaign
    ? presentCampaigns.map(camp => ({ color: campaignColors[camp], label: campaigns[camp] }))
    : cpxLegend(bands);
  return (
    <div className='quadrant-aside'>
      <div className='quadrant-aside__title'>{isCampaign ? 'Campaign' : 'CPX'}</div>
      <div className='quadrant-aside__items'>
        {items.map(item => (
          <span key={item.label} className='quadrant-legend__item'>
            <span className='legend-swatch' style={{ background: item.color }} />
            <span className='quadrant-legend__label'>{item.label}</span>
          </span>
        ))}
      </div>
      <div className='quadrant-aside__flag-note'>
        <span className='quadrant-aside__flag-ring' />
        Red outline = low diversity (needs attention)
      </div>
      <div className='quadrant-aside__flag-note'>
        <span className='quadrant-aside__imputed-ring' />
        Hollow = one score is N/A, plotted as 0
      </div>
    </div>
  );
};

const makeBubbleShape = (
  colorMode: ColorModeType,
  maxSpend: number,
  bands: CpxBands,
  campaignColors: string[]
) =>
  function BubbleShape({ cx = 0, cy = 0, payload }: ScatterShapeProps): ReactElement {
    const point = payload as QuadrantPoint | undefined;
    if (!point) return <g />;
    const { fill, stroke } = getBubbleColors(point, colorMode, bands, campaignColors);
    const r = bubbleRadius(point.spend, maxSpend);
    const flagged = isFlaggedQuadrantPoint(point);
    // One of the two axes was missing and plotted as 0. Render the bubble hollow so it
    // cannot be mistaken for an ad set that genuinely measured 0 on that axis. Hollow
    // already means "no reading" elsewhere on this page — per-ad delivery status uses the
    // same treatment — so the cue is consistent rather than new.
    const imputed = point.imputedX || point.imputedY;
    return (
      <g>
        {flagged && (
          <circle
            cx={cx}
            cy={cy}
            r={r + 3.5}
            fill='none'
            stroke={FLAG_COLOR}
            strokeWidth={2}
            strokeDasharray='3 2'
          />
        )}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill={imputed ? 'none' : fill}
          stroke={flagged ? FLAG_COLOR : stroke}
          strokeWidth={flagged ? 2 : 1.5}
          pointerEvents='all'
        />
      </g>
    );
  };

interface QuadrantChartProps {
  adSets: AdSet[];
  campaigns: string[];
  dateRange: DateRange;
  filtersActive: boolean;
  isLoading?: boolean;
}

export const QuadrantChart = ({
  adSets,
  campaigns,
  dateRange,
  filtersActive,
  isLoading
}: QuadrantChartProps) => {
  const [colorMode, setColorMode] = useState<ColorModeType>(ColorMode.CPX);
  const points = useMemo(() => buildQuadrantPoints(adSets, dateRange), [adSets, dateRange]);
  const maxSpend = useMemo(() => Math.max(1, ...points.map(point => point.spend)), [points]);
  // Terciles of the ad sets actually plotted, so the colouring ranks them against this
  // account rather than an absolute scale no client here would clear.
  const bands = useMemo(() => buildCpxBands(points), [points]);
  const campaignColors = useMemo(() => {
    const colorMap = customColorMapping(campaigns.map(name => ({ name })));
    return campaigns.map(name => colorMap[name]);
  }, [campaigns]);

  const isEmpty = points.length === 0;

  return (
    <ChartContainer
      title='Fatigue x Diversity quadrant'
      subHeader={`Bubble = ad set · size = spend · latest reading in range (${points.length} with fatigue history) · green zone = mid-range diversity, low fatigue`}
      minHeight={320}
      rightActions={
        <SegmentedControlButton
          options={COLOR_CPX_OPTIONS}
          selectedOption={colorMode}
          onChange={setColorMode}
          size={Size.SM}
          design={Design.NEUTRAL}
        />
      }
      viewRightActions={!isEmpty}
      isLoading={isLoading}
    >
      {isEmpty ? (
        <TableEmptyState
          message={emptyStateMessage(
            filtersActive,
            'No ad sets have both a fatigue reading and a diversity score in the selected date range.'
          )}
        />
      ) : (
        <div className='bp-chart-layout'>
          <div className='bp-chart-main'>
            <ResponsiveContainer width='100%' height={320}>
              <ScatterChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <CartesianGrid stroke={GRID_COLOR} />
                <XAxis
                  type='number'
                  dataKey='x'
                  domain={[0, 1]}
                  ticks={AXIS_TICKS}
                  tick={TICK_STYLE}
                  tickLine={false}
                  axisLine={false}
                  label={{
                    value: 'Diversity (0-1)  →  (more diverse)',
                    position: 'insideBottom',
                    offset: -4,
                    fill: Neutral600,
                    fontSize: 12
                  }}
                />
                <YAxis
                  type='number'
                  dataKey='y'
                  domain={[0, 1]}
                  ticks={AXIS_TICKS}
                  tick={TICK_STYLE}
                  tickLine={false}
                  axisLine={false}
                  label={{
                    value: 'Fatigue (0-1)  →',
                    angle: -90,
                    position: 'insideLeft',
                    fill: Neutral600,
                    fontSize: 12
                  }}
                  width={40}
                />
                {/* Healthy zone: mid-range diversity, low fatigue. */}
                <ReferenceArea
                  x1={DIVERSITY_HEALTHY_MIN}
                  x2={DIVERSITY_HEALTHY_MAX}
                  y1={0}
                  y2={FATIGUE_HEALTHY_MAX}
                  fill={Success700A30}
                />
                <ReferenceLine
                  x={DIVERSITY_HEALTHY_MIN}
                  stroke={Neutral600}
                  strokeDasharray='5 5'
                />
                <ReferenceLine
                  x={DIVERSITY_HEALTHY_MAX}
                  stroke={Neutral600}
                  strokeDasharray='5 5'
                />
                <ReferenceLine y={FATIGUE_HEALTHY_MAX} stroke={Neutral600} strokeDasharray='5 5' />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={<QuadrantTooltip campaigns={campaigns} />}
                />
                <Scatter
                  data={points}
                  isAnimationActive
                  shape={makeBubbleShape(colorMode, maxSpend, bands, campaignColors)}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <QuadrantLegend
            campaigns={campaigns}
            campaignColors={campaignColors}
            colorMode={colorMode}
            points={points}
            bands={bands}
          />
        </div>
      )}
    </ChartContainer>
  );
};
